import { useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import AcUnitOutlinedIcon from "@mui/icons-material/AcUnitOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import ParkOutlinedIcon from "@mui/icons-material/ParkOutlined";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import { generatePath, useNavigate } from "react-router-dom";

import { AppRoutes, landDetailLink } from "../../config";
import JudgeTermChallengeDialog from "./JudgeTermChallengeDialog";
import RespondToChallengeDialog from "../Challenge/RespondToChallengeDialog";
import { Settlement } from "../../hooks/useSettlement";
import useCovenantReviewState from "../../hooks/useCovenantReviewState";
import { useCountdown } from "../../services/CountDown";
import { formatCountdown } from "../../utils/countdown";
import { isUserRejection } from "../../services/relay.service";
import { challengeContractAddress } from "../../services/contract.service";
import {
  CHALLENGE_WINDOW_BLOCK_COPY,
  ChallengeState,
  challengeWindowBlock,
  describeChainError,
  releaseAfterReVerification,
  withdrawChallenge,
} from "../../services/challenge.service";
import { PopupActionButton, PopupDangerButton, PopupGhostButton, PopupProcessingButton } from "../PopupButtons";
import { ErrorNote, figmaButtonSx } from "./settlementStyles";

// The covenant card (V10.1, EcoChallenge) — everything that happens to a LIVE covenant after the
// sale: its review windows, the challenge raised in them, the drip freeze a breach finding causes,
// and the release halts. It exists in a state SettlementPanel deliberately stops rendering in
// (that panel returns null at "finalised" for everyone), hence a separate card — that panel is
// about the sale, this one is about the covenant after it.
//
// STATE COMES FROM CHAIN FIRST. useCovenantReviewState reads EcoBank.dripFrozen and EcoChallenge's
// windows / halts / challenges directly; the backend's `termChallenge` mirror (still keyed off
// EcoBank's V9 TermChallenged events) is only the fallback while the chain read is in flight or has
// failed. The mirror cannot tell a guardian WHY a release stopped, and V10.1 has three different
// reasons that need three different sentences (§3 and §5 of V10_Challenge_Paths):
//
//   frozen           an inBreach finding froze the TREE drip. Not lost — back-pay resumes on cure.
//                    Cure = a real re-verification, then releaseAfterReVerification. Copy says
//                    "awaiting re-verification", never "terminated" or "failed".
//   unattested halt  three consecutive review windows nobody attested or challenged (§5). The next
//                    attest or challenge resets the run; the next verification clears the halt.
//   better record    an upheld EVIDENCE finding halted releases until a better record is filed
//                    (C6). The next verification IS that record.
//
// What it offers, and to whom (frontend-side gates only — the contract is the real rule, and the
// dialogs surface its reverts as copy):
//
//   an independent verifier ("trust")  CHALLENGE — the review window is the period to do it in,
//     and the card counts it down. There is no attesting here: attesting is the one-time deed step
//     in SettlementPanel, and EcoChallenge.attest is deliberately not offered. One challenge per
//     wallet per token, ever. Never the covenant's own verifier or guardian — for them an open
//     window alone is no card.
//   the challenged verifier            RESPOND while the challenge is OPEN — one response, optional.
//   the challenger                     WITHDRAW while OPEN or RESPONDED — no flag, before seating.
//   a Trust Admin / GTA ("admin")      REVIEW — the drawn seats sign, anyone submits.
//   anyone                             RELEASE after re-verification, once the count has advanced
//     past the freeze. A backend job normally does this; offered as a fallback like finalise.

export interface CovenantChallengePanelProps {
  // On-chain token id (the Bank's settlement key — etNft.tokenId).
  tokenId: number | string;
  // The land's eco_token id (token.id). The challenge is raised from the land's verification page,
  // and /verification/:tokenId is keyed on this, not on the NFT id above.
  landId?: number | string | null;
  // Resolved settlement record. Prefetched by the page owner, like SettlementPanel's.
  settlement?: Settlement | null;
  // Connected wallet, for role-aware rendering. Authorization is ultimately on-chain — this only
  // decides what to *show*.
  viewerAddress?: string | null;
  // User.role from the backend. A hard gate for the verdict, exactly as in SettlementPanel.
  viewerRole?: string | null;
  // The land's verifier as the backend knows it (token.trust.address). The chain's seated verifier
  // wins once useCovenantReviewState lands; this covers the first paint, so the verifier never
  // sees the challenge card flash up on their own land before the read resolves.
  boundVerifier?: string | null;
  // Parent refetch after an action lands on-chain.
  onChanged?: () => void;
  sx?: SxProps<Theme>;
}

const STATE_LABEL: Record<number, string> = {
  [ChallengeState.OPEN]: "open — awaiting the verifier's response",
  [ChallengeState.RESPONDED]: "responded — panel not yet drawn",
  [ChallengeState.SEATED]: "panel seated — awaiting the determination",
};

const shortAddress = (addr: string) => `${addr.slice(0, 6)}…${addr.slice(-4)}`;

const CovenantChallengePanel = (props: CovenantChallengePanelProps) => {
  const { tokenId, landId, settlement, viewerAddress, viewerRole, boundVerifier, onChanged, sx } = props;
  const navigate = useNavigate();
  const [judgeOpen, setJudgeOpen] = useState(false);
  const [respondOpen, setRespondOpen] = useState(false);
  const [busy, setBusy] = useState<"withdraw" | "release" | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { state: review, refresh: refreshReview } = useCovenantReviewState(tokenId);

  const viewer = viewerAddress?.toLowerCase() ?? null;
  const termChallenge = settlement?.termChallenge ?? null;
  // Chain first, mirror as the fallback until the read lands.
  const frozen = review ? review.frozen : !!termChallenge?.frozen;
  const halted = !!review?.releasesHalted;
  const challenge = review?.challenge ?? null;
  const reviewWindow = review?.window ?? null;
  // Issue #628: a challenge is only offered while the window would take it — see
  // challengeWindowBlock. Judged on the chain's clock, not the viewer's. Null = open.
  const windowBlock = challengeWindowBlock(reviewWindow, Date.now() + (review?.chainClockOffsetMs ?? 0));
  const windowOpen = !!review && windowBlock === null;
  // useCountdown ticks every second, so the window line retires itself when the window closes.
  const windowCountdown = formatCountdown(useCountdown(windowOpen ? reviewWindow!.closesAt : null) as string[]);

  // The two wallets _requireIndependent refuses outright (EcoChallenge.sol:393-394). Both sides of
  // the verifier check count: the chain's seated verifier, and the backend's bound one until that
  // read lands (after a reseat the backend's may be the dismissed verifier, who is refused anyway).
  const isSeatedVerifier = !!viewer && (review?.verifier === viewer || boundVerifier?.toLowerCase() === viewer);
  const isGuardian = !!viewer && review?.guardian === viewer;

  // Frontend-side gate only. The contract's _requireIndependent is the real rule (different org,
  // different admitting holder, not the verifier or guardian) and the dialog surfaces its revert
  // as copy; "trust" is the role every registered verifier carries, and a holder/admin has no
  // verifier org so cannot raise one. The verifier and guardian are excluded here because they
  // are knowable without a round trip — offering the land's own verifier a challenge of their
  // own work is a dead end. The org / admitting-holder checks and the one-per-wallet check stay
  // with the contract and the dialog.
  //
  // And only while a review window is open (issue #628). V10.1.2 still ACCEPTS a challenge raised
  // outside one — it takes no window, can never hold anything, and is how several challenges
  // against one verification each add a flag — so the UI must not invite it; the next contract
  // release reverts it. An eligible verifier outside a window is told why instead of shown a
  // button, so the card does not simply vanish for them.
  //
  // The challenge itself is raised on the land's verification page (the record under review is
  // right there), which runs this same gate again — the button here only takes them to it.
  const eligibleChallenger = !!viewer && viewerRole === "trust" && !isSeatedVerifier && !isGuardian;
  const canChallenge = eligibleChallenger && windowOpen && landId !== null && landId !== undefined;
  // Determinations: seats are drawn from the country's Trust Admins and the GTA registry, so
  // "admin" is the only role that can ever hold one; the dialog says whether this wallet does.
  // Only offered while a live challenge stands — with nothing to review, the role alone must not
  // put a card on a healthy land page (it used to, showing "Review challenge" over "rewards active").
  const canJudge = !!viewer && viewerRole === "admin" && !!challenge;
  const isChallengedVerifier = !!viewer && !!challenge && challenge.verifier.toLowerCase() === viewer;
  const isChallenger = !!viewer && !!challenge && challenge.challenger.toLowerCase() === viewer;
  const canRespond = isChallengedVerifier && challenge?.state === ChallengeState.OPEN;
  const canWithdraw =
    isChallenger && (challenge?.state === ChallengeState.OPEN || challenge?.state === ChallengeState.RESPONDED);
  // The release needs the DETERMINED challenge's id and a verification count past the freeze —
  // exactly the two things releaseAfterReVerification checks (NotReVerified otherwise). It
  // releases BOTH a frozen drip and a held deed settlement (EcoChallenge.sol:962-984), so it is
  // offered whenever either is standing and the count has advanced.
  const releaseTarget =
    (frozen || review?.heldSettlement) && review?.reVerified && review.lastDetermined ? review.lastDetermined : null;
  // Ravi's review, finding 3 (fixed in the next contract release): releaseAfterReVerification is
  // permissionless and today ALSO lifts a freeze a GTA put on directly, not only one a panel's
  // breach finding put on. EcoBank's TermChallenged(tokenId, challenger) names who froze it — the
  // backend mirrors the latest one as termChallenge.challengedBy — so a freeze is only offered for
  // release when that is EcoChallenge. Unknown (not ingested yet, or the chain-only path) means
  // wait, exactly as the backend's release job does; a held settlement with no freeze needs no test.
  const frozenBy = termChallenge?.challengedBy?.toLowerCase() ?? null;
  const freezeFromPanel = frozenBy === challengeContractAddress.toLowerCase();
  const canRelease = !!viewer && !!releaseTarget && (!frozen || freezeFromPanel);
  const releaseRefusedForGtaFreeze = !!releaseTarget && frozen && !!frozenBy && !freezeFromPanel;
  // An upheld FIELD finding moved the seat and the dismissed verifier is still seated (V10.1
  // SeatTransferRequired). The forced order is dismiss → reseat → re-verify → unfreeze
  // (V10_Challenge_Paths §3): nothing here can be done until the Trust Admin runs the first two
  // in the Portal, so the card says so instead of "awaiting re-verification" alone.
  const reseatPending = !!review?.reseatRequired;

  // Worth a card when the covenant is live and there is either something to show (a freeze, a
  // halt, a standing challenge, an open window) or something the viewer can do. The chain signals
  // count regardless of the mirror's `state` — a freeze is a fact about the token. An open window
  // is an invitation to OTHER verifiers to review this one's work, so it does not put the card in
  // front of the seated verifier; a challenge, freeze, halt or reseat against their covenant does.
  const chainSignal =
    frozen || halted || !!challenge || (windowOpen && !isSeatedVerifier) || reseatPending || !!review?.heldSettlement;
  const finalised = settlement?.state === "finalised";
  if (!finalised && !chainSignal) {
    return null;
  }
  if (!chainSignal && !termChallenge && !eligibleChallenger && !canJudge) {
    return null;
  }

  const changed = async () => {
    await refreshReview();
    onChanged?.();
  };

  const runAction = async (kind: "withdraw" | "release", action: () => Promise<string>) => {
    setBusy(kind);
    setActionError(null);
    try {
      await action();
      await changed();
    } catch (e: any) {
      if (!isUserRejection(e)) {
        setActionError(describeChainError(e));
      }
    } finally {
      setBusy(null);
    }
  };

  // Which of the three reasons a release can stop applies, or the healthy line. Frozen outranks a
  // halt because it is the one with money attached (the drip), and both outrank the challenge line.
  let banner: { bg: string; fg: string; icon: JSX.Element; title: string };
  let statusCopy: string;
  if (frozen) {
    banner = {
      bg: "#FFF4E5",
      fg: "#7A4100",
      icon: <AcUnitOutlinedIcon sx={{ color: "#B76E00", flexShrink: 0 }} />,
      title: "TREE rewards frozen",
    };
    statusCopy = review?.reVerified
      ? "The covenant was found in breach and has since been re-verified. The frozen rewards can now be released — back-pay resumes from the freeze."
      : reseatPending
      ? "The covenant was found in breach and its verification removed. Rewards are paused, not lost: the Trust Admin " +
        "dismisses the seated verifier and reseats the covenant on the challenger, whose re-verification restores them " +
        "with back-pay."
      : "The covenant was found in breach and is awaiting re-verification. Rewards are paused, not lost: one " +
        "verification by the seated verifier restores them and back-pay resumes.";
  } else if (halted && review?.awaitingBetterRecord) {
    banner = {
      bg: "#FDECEA",
      fg: "#7A1F1F",
      icon: <PauseCircleOutlineIcon sx={{ color: "#B3261E", flexShrink: 0 }} />,
      title: "Fund release halted",
    };
    statusCopy =
      "An evidence challenge was upheld: the record does not hold up, and fund release halts until a better record " +
      "is filed. The next verification is that record.";
  } else if (halted) {
    banner = {
      bg: "#FDECEA",
      fg: "#7A1F1F",
      icon: <PauseCircleOutlineIcon sx={{ color: "#B3261E", flexShrink: 0 }} />,
      title: "Fund release halted",
    };
    statusCopy =
      `${
        review?.unattestedRuns ?? review?.unattestedHaltAt ?? 3
      } review windows in a row closed without an independent review of the verification, so fund release has ` +
      "stopped.";
  } else {
    banner = {
      bg: "#EDF7ED",
      fg: "#1E4620",
      icon: <ParkOutlinedIcon sx={{ color: "#2E7D32", flexShrink: 0 }} />,
      title: "TREE rewards active",
    };
    statusCopy = challenge
      ? "This covenant's verification is under challenge. Rewards keep running unless the panel finds the covenant in breach."
      : reseatPending
      ? "A field challenge was upheld: the verification was removed and the seat moves to the challenger. Rewards keep " +
        "running; the Trust Admin dismisses the seated verifier and reseats the covenant, and the new verifier re-verifies it."
      : termChallenge
      ? "A previous challenge on this covenant was determined — TREE rewards are running normally."
      : "This easement is active and its TREE rewards are running normally.";
  }

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "20px",
        border: "none",
        padding: { xs: "16px", sm: "24px" },
        ...sx,
      }}
    >
      <p
        style={{
          fontWeight: 600,
          fontSize: "14px",
          margin: "0 0 16px",
          fontFamily: "monospace",
          textTransform: "uppercase",
        }}
      >
        Covenant
      </p>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          backgroundColor: banner.bg,
          borderRadius: "12px",
          padding: { xs: "12px 14px", sm: "12px 16px" },
        }}
      >
        {banner.icon}
        <p style={{ fontSize: "14px", color: banner.fg, margin: 0 }}>{banner.title}</p>
      </Box>

      <p style={{ fontSize: "14px", lineHeight: 1.6, color: "#444", margin: "16px 0 0" }}>{statusCopy}</p>

      {/* The standing challenge, from chain. */}
      {challenge && (
        <p style={{ fontSize: "13px", color: "#888", margin: "6px 0 0" }}>
          Challenge #{challenge.challengeId.toString()} by {shortAddress(challenge.challenger)} —{" "}
          {STATE_LABEL[challenge.state] ?? "standing"}
          {challenge.openedAt ? ` · raised ${challenge.openedAt.toLocaleDateString()}` : ""}
        </p>
      )}
      {!challenge && termChallenge?.challengedAt && (
        <p style={{ fontSize: "13px", color: "#888", margin: "6px 0 0" }}>
          Challenged {termChallenge.challengedAt.toLocaleDateString()}
          {termChallenge.judgedAt
            ? ` · Judged ${termChallenge.judgedAt.toLocaleDateString()} — ${
                termChallenge.upheld ? "upheld" : "dismissed"
              }`
            : ""}
        </p>
      )}

      {/* The open review window: who may act, and for how long. */}
      {windowOpen && windowCountdown && (
        <p style={{ fontSize: "13px", color: "#888", margin: "6px 0 0" }}>
          Review window open — an independent verifier may challenge this verification for another{" "}
          <span style={{ fontFamily: "monospace" }}>{windowCountdown}</span>.
        </p>
      )}

      {actionError && <ErrorNote>{actionError}</ErrorNote>}

      {/* An independent verifier: dispute the verification. Challenging is the only action the
          review window exists for. */}
      {canChallenge && (
        <Box sx={{ mt: "16px", display: "flex", flexDirection: "column", gap: "10px" }}>
          <PopupDangerButton
            onClick={() =>
              navigate(generatePath(AppRoutes.Verification.path, { tokenId: String(landId) }), {
                state: { ref: landDetailLink(String(landId)) },
              })
            }
            disabled={busy !== null}
            fullWidth
          >
            Challenge this verification
          </PopupDangerButton>
          <p style={{ fontSize: "13px", color: "#888", textAlign: "center", margin: 0 }}>
            For an independent verifier who has been to the land or reviewed the record. Sent as a wallet transaction.
          </p>
        </Box>
      )}
      {eligibleChallenger && !canChallenge && review && windowBlock && (
        <p style={{ fontSize: "13px", color: "#888", margin: "16px 0 0" }}>
          {CHALLENGE_WINDOW_BLOCK_COPY[windowBlock]}
        </p>
      )}

      {/* The challenged verifier: one optional response while the challenge is open. */}
      {canRespond && (
        <Box sx={{ mt: "16px" }}>
          <PopupActionButton onClick={() => setRespondOpen(true)} variant="contained" fullWidth sx={figmaButtonSx}>
            Respond to the challenge — pays gas
          </PopupActionButton>
        </Box>
      )}

      {/* The challenger: withdraw before a panel is seated. */}
      {canWithdraw && (
        <Box sx={{ mt: "16px" }}>
          {busy === "withdraw" ? (
            <PopupProcessingButton disabled variant="contained" fullWidth sx={figmaButtonSx}>
              <CircularProgress size={20} sx={{ color: "white", mr: "10px" }} />
              Confirm the transaction in your wallet…
            </PopupProcessingButton>
          ) : (
            <PopupGhostButton
              onClick={() => runAction("withdraw", () => withdrawChallenge(challenge!.challengeId))}
              disabled={busy !== null}
              fullWidth
            >
              Withdraw your challenge
            </PopupGhostButton>
          )}
          <p style={{ fontSize: "13px", color: "#888", textAlign: "center", margin: "10px 0 0" }}>
            Possible until the panel is drawn. No flag for withdrawing. Wallet transaction — you pay the gas.
          </p>
        </Box>
      )}

      {/* Trust Admins / GTAs: review whatever challenge is standing. */}
      {canJudge && (
        <Box sx={{ mt: "16px" }}>
          <PopupActionButton onClick={() => setJudgeOpen(true)} variant="contained" fullWidth>
            <GavelOutlinedIcon sx={{ fontSize: 18, mr: "8px", flexShrink: 0 }} />
            Review challenge
          </PopupActionButton>
        </Box>
      )}

      {/* Anyone: release once the covenant has been re-verified past the freeze. A backend job
          normally does this; the button is the fallback for when it is down or behind. */}
      {canRelease && (
        <Box sx={{ mt: "16px" }}>
          {busy === "release" ? (
            <PopupProcessingButton disabled variant="contained" fullWidth sx={figmaButtonSx}>
              <CircularProgress size={20} sx={{ color: "white", mr: "10px" }} />
              Confirm the transaction in your wallet…
            </PopupProcessingButton>
          ) : (
            <PopupActionButton
              onClick={() =>
                runAction("release", () => releaseAfterReVerification(tokenId, releaseTarget!.challengeId))
              }
              disabled={busy !== null}
              variant="contained"
              fullWidth
              sx={figmaButtonSx}
            >
              {frozen ? "Release frozen rewards — pays gas" : "Release the held settlement — pays gas"}
            </PopupActionButton>
          )}
          <p style={{ fontSize: "13px", color: "#888", textAlign: "center", margin: "10px 0 0" }}>
            The covenant has been re-verified since the finding. A backend job normally releases it; this only exists in
            case that is behind.
          </p>
        </Box>
      )}
      {releaseRefusedForGtaFreeze && (
        <p style={{ fontSize: "13px", color: "#888", margin: "16px 0 0" }}>
          This freeze was put on directly by a Global Trust Admin, not by a panel&apos;s breach finding, so it is not
          released by re-verification here.
        </p>
      )}

      <JudgeTermChallengeDialog
        open={judgeOpen}
        onClose={() => setJudgeOpen(false)}
        tokenId={tokenId}
        viewerAddress={viewerAddress}
        onJudged={() => changed()}
      />

      {challenge && (
        <RespondToChallengeDialog
          open={respondOpen}
          onClose={() => setRespondOpen(false)}
          stage="covenant"
          id={challenge.challengeId}
          challengedBy={challenge.challenger}
          onResponded={() => changed()}
        />
      )}
    </Box>
  );
};

export default CovenantChallengePanel;
